import _ from 'lodash';
import i18n from 'i18next';

/*------------------------------------------------------------------------------
 */
Mobi2Go._class = Mobi2Go._class || {};

Mobi2Go._class.Common = {
    Discount,
    Price,
    Collection,
};

/*------------------------------------------------------------------------------
 * Price helper
 */
function Price(amount, tax) {
    if (!(this instanceof Price)) {
        return new Price(amount);
    }

    this.set(amount);
    this.setTax(tax);
}
Price.prototype.set = function(amount) {
    if (amount instanceof Price) {
        this.setTax(amount.tax);
        amount = amount.toFloat();
    }

    amount = amount || 0;
    if (isNaN(amount - 0)) {
        throw new TypeError(
            `${amount} is invalid, number required for a new Price`
        );
    }

    // Float e.g. 4.5
    this[0] = +amount;

    let fixed_amount = this[0].toFixed(2);
    if (+fixed_amount === -0.0) {
        fixed_amount = '0.00';
    }

    // String with currency symbol e.g. '$4.50'
    this[1] = Mobi2Go.headoffice.currency_symbol + fixed_amount;
    // String with currency symbol and code e.g. '4.50NZD'
    this[2] = `${fixed_amount}${Mobi2Go.headoffice.currency_abrv}`;
    this[3] = Mobi2Go.headoffice.currency_abrv;

    this.amount = this[0];
    this.formatted = this[1];
    this.international = this[2];
    this.currency_code = this[3];

    return this;
};
Price.prototype.toString = function() {
    return this[1];
};
Price.prototype.format = function(format) {
    format = format || '$0.00';
    return numeral(this[0]).format(format);
};
Price.prototype.toInternational = function() {
    return this[2];
};
Price.prototype.add = function(amount) {
    if (amount instanceof Price) {
        amount = amount[0];
    }
    return this.set(this[0] + +amount);
};
Price.prototype.subtract = function(amount) {
    if (amount instanceof Price) {
        amount = amount[0];
    }
    return this.set(this[0] - +amount);
};
Price.prototype.multiply = function(amount) {
    return this.set(this[0] * +amount);
};
Price.prototype.isEqual = function(comparator) {
    if (comparator instanceof Price) {
        comparator = comparator[0];
    }
    return this[0] === +comparator;
};
Price.prototype.toFloat = function() {
    return this[0];
};
Price.prototype.clone = function() {
    return new Price(this[0], this.tax);
};
Price.prototype.getTax = function() {
    return this.tax;
};
Price.prototype.setTax = function(tax) {
    this.tax = tax;

    return this;
};
Price.prototype.getTaxExclusivePrice = function() {
    if (this.tax) {
        return this.clone()
            .setTax(null)
            .subtract(this.getTaxPrice());
    }

    return this;
};
Price.prototype.getTaxInclusivePrice = function() {
    return this;
};
Price.prototype.getCurrencyCode = function() {
    return this.currency_code;
};
Price.prototype.isTaxExclusivePrice = function() {
    if (this.tax && this.tax.has_tax_exclusive_prices) {
        return true;
    }
    return false;
};
(Price.prototype.getListPrice = function() {
    if (this.isTaxExclusivePrice()) {
        return this.getTaxExclusivePrice();
    }
    return this.getTaxInclusivePrice();
}),
    (Price.prototype.getTaxPrice = function() {
        if (this.tax && this.tax.rate) {
            let price = this.toFloat() / (100 / (this.tax.rate * 100) + 1);
            price = this.round(price);
            return new Price(price);
        }

        return new Price(0.0);
    });
Price.prototype.applyTaxAmount = function() {
    if (this.isTaxExclusivePrice()) {
        const tax_amount = this.round(this.toFloat() * this.tax.rate);
        this.add(tax_amount);
    }

    return this;
};

Price.prototype.round = function(amount) {
    return Math.round(amount * 10000) / 10000;
};

/*------------------------------------------------------------------------------
 * Discount helper
 */
function Discount(amount, tax, tax_amount) {
    if (!(this instanceof Discount)) {
        return new Discount(amount, tax, tax_amount);
    }

    this.set(amount);
    this.setTax(tax);
    this.setTaxAmount(tax_amount);
}
Discount.prototype.set = function(amount, tax_amount) {
    if (amount instanceof Discount) {
        this.setTax(amount.getTax());
        amount = amount.toFloat();
        tax_amount = amount.getTaxAmount();
    }

    amount = amount || 0;

    if (isNaN(amount - 0)) {
        throw new TypeError(
            `${amount} is invalid, number required for a new Discount`
        );
    }
    tax_amount = tax_amount || 0;
    if (isNaN(tax_amount - 0)) {
        throw new TypeError(
            `${tax_amount} is invalid, number required for a new Discount`
        );
    }

    this.setTaxAmount(tax_amount);

    // Float e.g. 4.5
    this[0] = +amount;
    // String with currency symbol e.g. '$4.50'
    this[1] = Mobi2Go.headoffice.currency_symbol + this[0].toFixed(2);
    // String with currency symbol and code e.g. '4.50NZD'
    this[2] = `${this[0].toFixed(2)}${Mobi2Go.headoffice.currency_abrv}`;

    return this;
};
Discount.prototype.toString = function() {
    return this[1];
};
Discount.prototype.format = function(format) {
    format = format || '$0.00';
    return numeral(this[0]).format(format);
};
Discount.prototype.toInternational = function() {
    return this[2];
};
Discount.prototype.add = function(amount, tax_amount) {
    if (amount instanceof Discount) {
        amount = amount[0];
        tax_amount = amount.tax_amount;
    }
    return this.set(this[0] + +amount, this.tax_amount + +tax_amount);
};
Discount.prototype.subtract = function(amount, tax_amount) {
    if (amount instanceof Discount) {
        amount = amount[0];
        tax_amount = amount.tax_amount;
    }
    return this.set(this[0] - +amount, this.tax_amount - +tax_amount);
};
Discount.prototype.multiply = function(amount) {
    return this.set(this[0] * +amount, this.tax_amount * +amount);
};
Discount.prototype.isEqual = function(comparator, tax_amount) {
    if (comparator instanceof Discount) {
        return (
            this[0] === +comparator[0] &&
            this.tax_amount === +comparator.tax_amount
        );
    }
    return this[0] === +comparator && this.tax_amount === (tax_amount || 0);
};
Discount.prototype.toFloat = function() {
    return this[0];
};
Discount.prototype.clone = function() {
    return new Discount(this[0], this.tax, this.tax_amount);
};
Discount.prototype.getTax = function() {
    return this.tax;
};
Discount.prototype.setTax = function(tax) {
    this.tax = tax;

    return this;
};
Discount.prototype.getTaxAmount = function() {
    return this.tax_amount;
};
Discount.prototype.setTaxAmount = function(tax_amount) {
    this.tax_amount = tax_amount;

    return this;
};
Discount.prototype.getTaxExclusiveDiscount = function() {
    if (this.tax) {
        return this.clone()
            .setTax(null)
            .setTaxAmount(0)
            .subtract(this.getTaxAmount());
    }

    return this;
};
Discount.prototype.getTaxInclusiveDiscount = function() {
    return this;
};
Discount.prototype.isTaxExclusiveDiscount = function() {
    if (this.tax && this.tax.has_tax_exclusive_prices) {
        return true;
    }
    return false;
};
(Discount.prototype.getListDiscount = function() {
    if (this.isTaxExclusiveDiscount()) {
        return this.getTaxExclusiveDiscount();
    }
    return this.getTaxInclusiveDiscount();
}),
    (Discount.prototype.round = function(amount) {
        return Math.round(amount * 10000) / 10000;
    });

/*------------------------------------------------------------------------------
 * Collection object
 */
function Collection(items) {
    if (!(this instanceof Collection)) {
        return new Collection(items);
    }

    this.collection = [];
    this.set(items);
}

Collection.prototype.get = function() {
    return this.collection;
};

Collection.prototype.getLength = function() {
    return this.collection.length;
};

Collection.prototype.set = function(items) {
    this.collection = items;
};

Collection.prototype.getById = function(id) {
    return this.find({ id: +id });
};

Collection.prototype.getByName = function(name) {
    if (!_.isString(name)) {
        return [];
    }
    name = name.toLowerCase();

    return this.find(function(object) {
        if (_.isString(object.name)) {
            return object.name.toLowerCase() === name;
        }
        return false;
    });
};

Collection.prototype.getByBackendName = function(name) {
    if (!_.isString(name)) {
        return [];
    }
    name = name.toLowerCase();

    return this.find(function(object) {
        if (_.isString(object.name)) {
            return object.backend_name?.toLowerCase() === name;
        }
        return false;
    });
};

Collection.prototype.push = function(item) {
    this.collection.push(item);
};

Collection.prototype.sort = function(fn) {
    return this.collection.sort(fn);
};

// Extend prototype with some delicious underscore functions
const us_functions = [
    'each',
    'filter',
    'map',
    'reduce',
    'reduceRight',
    'find',
    'where',
    'reject',
    'chain',
    'groupBy',
    'sortBy',
    'size',
    'first',
    'last',
    'toArray',
    'invokeMap',
    'remove',
    'reject',
];
_.each(us_functions, function(fn) {
    Collection.prototype[fn] = function() {
        const args = _.toArray(arguments); // arguments object can't be concat'd
        return _[fn].apply(this, [this.collection].concat(args));
    };
});

// Helpers - a collection of helpful functions!
const email_regex = /^[-!#$%&'*+\/0-9=?A-Z^_a-z{|}~](\.?[-!#$%&'*+/0-9=?A-Z^_a-z`{|}~])*@[a-zA-Z0-9](-?[a-zA-Z0-9])*(\.[a-zA-Z](-?[a-zA-Z0-9])*)+$/;

const time_display_value_cache = {};

_.extend(Mobi2Go.helpers, {
    formatDatetimeToTimeDisplayValue(datetime) {
        const minutes_since_start_of_day =
            datetime.hours() * 60 + datetime.minutes();
        if (!(minutes_since_start_of_day in time_display_value_cache)) {
            time_display_value_cache[
                minutes_since_start_of_day
            ] = i18n.t('common:time', { time: datetime });
        }
        return time_display_value_cache[minutes_since_start_of_day];
    },

    /**-------------------------------------------------------------------------
     */
    getCurrentTimestamp() {
        return +new Date();
    },

    /**-------------------------------------------------------------------------
     * Returns the distance in km between two gps coordinates
     */
    calculateDistance(lat1, lon1, lat2, lon2) {
        const pi = Math.PI;
        const R = 6371; // km
        const dLat = ((lat1 - lat2) * pi) / 180;
        const dLon = ((lon1 - lon2) * pi) / 180;
        const a =
            Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos((lat1 * pi) / 180) *
                Math.cos((lat2 * pi) / 180) *
                Math.sin(dLon / 2) *
                Math.sin(dLon / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        const d = Math.round(R * c * 10) / 10;
        return d;
    },

    /**-------------------------------------------------------------------------
     * Converts an hour string from 24 to 12 hour format e.g. '22:00' to '10:00pm'
     */
    AMPMise(time) {
        const split = time.split(':');
        let hour = +split[0];
        const minute = split[1];
        let suffix = 'am';

        if (hour === 12) {
            return `${time}pm`;
        }
        if (hour > 12) {
            hour -= 12;
            suffix = 'pm';
        }
        return `${Mobi2Go.helpers.pad(hour, 2)}:${minute}${suffix}`;
    },

    /**
     * Pads a string with {size} zeros.
     */
    pad(target, size) {
        let s = String(target);
        while (s.length < (size || 2)) {
            s = `0${s}`;
        }
        return s;
    },

    /**
     * Converts first letter of the string to uppercase.
     */
    capitalise(str, lowercase_rest) {
        str = str == null ? '' : `${str}`;
        const remaining_chars = !lowercase_rest
            ? str.slice(1)
            : str.slice(1).toLowerCase();

        return str.charAt(0).toUpperCase() + remaining_chars;
    },

    /**-------------------------------------------------------------------------
     */
    validator: {
        email(string) {
            function isEmailValid(email) {
                if (!email) {
                    return false;
                }

                if (email.length > 254) {
                    return false;
                }

                const valid = email_regex.test(email);
                if (!valid) {
                    return false;
                }

                // Further checking of some things regex can't handle
                const parts = email.split('@');
                if (parts[0].length > 64) {
                    return false;
                }

                const domainParts = parts[1].split('.');
                if (
                    domainParts.some(function(part) {
                        return part.length > 63;
                    })
                ) {
                    return false;
                }

                return true;
            }

            return {
                is_valid: isEmailValid(string),
            };
        },
        password(string) {
            let is_valid = true;
            let message = '';

            if (!string || string.length < 6) {
                message = 'password must be at least 6 characters';
                is_valid = false;
            }

            return {
                is_valid,
                message,
            };
        },
        phoneNumber(string) {
            let is_valid = true;
            let message = '';

            if (!string || string.length < 5 || string.length > 14) {
                message = 'invalid phone number';
                is_valid = false;
            }

            return {
                is_valid,
                message,
            };
        },
    },

    /**-------------------------------------------------------------------------
     */
    sanitiser: {
        email(email) {
            return email
                .replace(/,/g, '.')
                .replace(/ /g, '')
                .replace(/\.\./g, '.');
        },
        phoneNumber(phone_number) {
            return phone_number.replace(/[^0-9]/g, '').substr(0, 12);
        },
    },

    /**-------------------------------------------------------------------------
     */
    classNames() {
        const classes = [];

        for (let i = 0; i < arguments.length; i++) {
            const arg = arguments[i];
            if (!arg) continue;

            const argType = typeof arg;

            if (argType === 'string' || argType === 'number') {
                classes.push(arg);
            } else if (Array.isArray(arg)) {
                classes.push(Mobi2Go.helpers.classNames.apply(null, arg));
            } else if (argType === 'object') {
                for (const key in arg) {
                    if ({}.hasOwnProperty.call(arg, key) && arg[key]) {
                        classes.push(key);
                    }
                }
            }
        }

        return classes.join(' ');
    },
});

export { Price, Discount };
